import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaSearch, FaSync, FaFileExport, FaCalendarAlt, FaCheck, FaSpinner, FaTimes, FaBoxOpen, FaEnvelope, FaInfoCircle } from "react-icons/fa";
import moment from "moment";
import ExcelJS from "exceljs";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { InquiryService } from "../../../service/admin/inquiryService";
import { formatDate } from "../../../helper/utils";
import { getAuthLocalStorage } from "../../../storage/localStorage";
import ExcelTable from "../../../component/library/excelTable";
import NoImage from "../../../assets/images/common/importerr-no-image-logo.png";
import InquiryNotesModal from "./InquiryNotesModal";
import "./style.css";

/* ─── helpers ─── */
const statusBadgeClass = (s) => {
  if (!s) return "neutral";
  if (s === "open") return "warning";
  if (s === "responded") return "info";
  if (s === "closed") return "success";
  return "neutral";
};

/* ─── Export-progress bar (same pattern as User Management) ─── */
const ExportProgress = ({ progress, isExporting, status, onClose }) => {
  if (!isExporting) return null;
  const icons = {
    fetching: <FaSpinner className="ep-icon ep-spinning" />,
    processing: <FaSpinner className="ep-icon ep-spinning" />,
    downloading: <FaSpinner className="ep-icon ep-spinning" />,
    completed: <FaCheck className="ep-icon ep-completed" />,
    error: <FaTimes className="ep-icon ep-error" />,
  };
  const texts = {
    fetching: "Fetching data...",
    processing: "Processing data...",
    downloading: "Generating file...",
    completed: "Export completed!",
    error: "Export failed",
  };
  return (
    <div className="export-progress-container">
      <div className="export-progress-card">
        <div className="export-progress-header">
          <div className="export-progress-title-section">
            {icons[status] || icons.fetching}
            <span className="export-progress-title">Export</span>
          </div>
          {(status === "completed" || status === "error") && (
            <button className="export-progress-close" onClick={onClose} title="Close">✕</button>
          )}
        </div>
        <div className="export-progress-status">
          <span className="export-progress-step">{texts[status] || "Exporting..."}</span>
        </div>
        <div className="export-progress-bar-wrapper">
          <div className="export-progress-bar">
            <div className="export-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
        <div className="export-progress-percentage">{progress}%</div>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────── */
const AdminInquiryList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const message = useMessage();
  const authData = getAuthLocalStorage();
  const currentUserId = authData?.loginHeader || null;

  /* ── Tab ── */
  const [activeTab, setActiveTab] = useState("pdp");
  useEffect(() => {
    setActiveTab(location.pathname.includes("/admin/contacts") ? "contact" : "pdp");
  }, [location.pathname]);

  /* ─── PDP state ─── */
  const [pdpSearch, setPdpSearch] = useState("");
  const [pdpStatus, setPdpStatus] = useState("");
  const [pdpInquiryType, setPdpInquiryType] = useState("");
  const [pdpData, setPdpData] = useState([]);
  const [pdpPage, setPdpPage] = useState(1);
  const [pdpLimit, setPdpLimit] = useState(20);
  const [pdpTotal, setPdpTotal] = useState(0);
  const [pdpPages, setPdpPages] = useState(0);
  const [pdpLoading, setPdpLoading] = useState(false);

  /* ─── Contact state ─── */
  const [ctSearch, setCtSearch] = useState("");
  const [ctStatus, setCtStatus] = useState("");
  const [ctData, setCtData] = useState([]);
  const [ctPage, setCtPage] = useState(1);
  const [ctLimit, setCtLimit] = useState(20);
  const [ctTotal, setCtTotal] = useState(0);
  const [ctPages, setCtPages] = useState(0);
  const [ctLoading, setCtLoading] = useState(false);

  /* ─── Export state ─── */
  const [exportDateRange, setExportDateRange] = useState({ startDate: "", endDate: "" });
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportStatus, setExportStatus] = useState("fetching");
  const [showExportProgress, setShowExportProgress] = useState(false);

  /* ─── Notes modal ─── */
  const [notesModal, setNotesModal] = useState({ open: false, itemId: null, notes: [], type: "inquiry" });

  /* ═══════════════ PDP Fetch ═══════════════ */
  const pdpQuery = useMemo(
    () => ({ q: pdpSearch, status: pdpStatus, inquiryType: pdpInquiryType, page: pdpPage, limit: pdpLimit }),
    [pdpSearch, pdpStatus, pdpInquiryType, pdpPage, pdpLimit]
  );

  const fetchPdp = useCallback(async (q) => {
    setPdpLoading(true);
    try {
      const res = await InquiryService.getInquiries(q || pdpQuery);
      if (res?.status?.code === 200) {
        setPdpData(res.data?.results || []);
        setPdpTotal(res.data?.total || 0);
        setPdpPages(res.data?.totalPages || 0);
      } else if (res?.status?.code === 402) {
        navigate("/login");
      } else {
        message.showToast(res?.status?.message || "Failed to fetch inquiries", ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || "Failed to fetch inquiries", ToastTypes.ERROR);
    } finally {
      setPdpLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdpQuery]);

  useEffect(() => { if (activeTab === "pdp") fetchPdp(); }, [activeTab, fetchPdp]);

  /* ═══════════════ Contact Fetch ═══════════════ */
  const ctQuery = useMemo(
    () => ({ q: ctSearch, status: ctStatus, page: ctPage, limit: ctLimit }),
    [ctSearch, ctStatus, ctPage, ctLimit]
  );

  const fetchCt = useCallback(async (q) => {
    setCtLoading(true);
    try {
      const res = await InquiryService.getContacts(q || ctQuery);
      if (res?.status?.code === 200) {
        setCtData(res.data?.results || []);
        setCtTotal(res.data?.total || 0);
        setCtPages(res.data?.totalPages || 0);
      } else if (res?.status?.code === 402) {
        navigate("/login");
      } else {
        message.showToast(res?.status?.message || "Failed to fetch contacts", ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || "Failed to fetch contacts", ToastTypes.ERROR);
    } finally {
      setCtLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctQuery]);

  useEffect(() => { if (activeTab === "contact") fetchCt(); }, [activeTab, fetchCt]);

  /* ═══════════════ Notes helpers ═══════════════ */
  const openNotes = (item, type) => {
    setNotesModal({
      open: true,
      itemId: item._id || item.id,
      notes: item.adminNotes || [],
      type,
    });
  };

  const handleNotesUpdated = (updatedItem) => {
    if (notesModal.type === "inquiry") {
      setPdpData((prev) =>
        prev.map((d) =>
          (d._id || d.id) === (updatedItem._id || updatedItem.id)
            ? { ...d, adminNotes: updatedItem.adminNotes }
            : d
        )
      );
    } else {
      setCtData((prev) =>
        prev.map((d) =>
          d._id === updatedItem._id ? { ...d, adminNotes: updatedItem.adminNotes } : d
        )
      );
    }
    setNotesModal((p) => ({ ...p, notes: updatedItem.adminNotes || [] }));
  };

  /* ═══════════════ Excel export helpers ═══════════════ */
  const buildExcelAndDownload = async (rows, sheetColumns, filename, onProgress) => {
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet("Data");
    ws.columns = sheetColumns;
    const headerRow = ws.getRow(1);
    headerRow.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
    headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4472C4" } };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };
    headerRow.height = 20;

    rows.forEach((r, i) => {
      const row = ws.addRow(r);
      row.eachCell((cell) => {
        cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
        cell.alignment = { vertical: "middle", horizontal: "left" };
      });
      if (onProgress && (i + 1) % 20 === 0) {
        onProgress({ progress: Math.round(((i + 1) / rows.length) * 70) + 10, status: "processing" });
      }
    });

    if (onProgress) onProgress({ progress: 80, status: "downloading" });
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    if (onProgress) onProgress({ progress: 100, status: "completed" });
  };

  /* --- PDP export --- */
  const pdpExcelColumns = [
    { header: "User Name", key: "userName", width: 20 },
    { header: "Email", key: "email", width: 25 },
    { header: "Phone", key: "phone", width: 15 },
    { header: "SKU", key: "sku", width: 18 },
    { header: "Total Qty", key: "totalQuantity", width: 12 },
    { header: "Last Message", key: "lastMessage", width: 40 },
    { header: "Status", key: "status", width: 12 },
    { header: "PDP Inquiry", key: "inquiryType", width: 18 },
    { header: "Inquiry Date", key: "createdAt", width: 20 },
    { header: "Status Date", key: "updatedAt", width: 20 },
    { header: "Admin Notes", key: "adminNotes", width: 30 },
  ];

  const mapPdpRow = (item) => ({
    userName: item?.user?.fullName || item?.user?.firstName || "-",
    email: item?.user?.email || "-",
    phone: item?.user?.phone || "-",
    sku: item?.productSku || "-",
    totalQuantity: item?.totalQuantity ?? "-",
    lastMessage: item?.lastMessage || "-",
    status: item?.status || "-",
    inquiryType: item?.inquiryType || "General Query",
    createdAt: item?.createdAt ? moment(item.createdAt).format("DD/MM/YYYY HH:mm") : "-",
    updatedAt: item?.lastMessageAt || item?.updatedAt ? moment(item.lastMessageAt || item.updatedAt).format("DD/MM/YYYY HH:mm") : "-",
    adminNotes: (item?.adminNotes || []).map((n) => `[${n.adminName}] ${n.note}`).join(" | ") || "-",
  });

  /* --- Contact export --- */
  const ctExcelColumns = [
    { header: "Name", key: "name", width: 20 },
    { header: "Email", key: "email", width: 25 },
    { header: "Phone", key: "phone", width: 15 },
    { header: "Subject", key: "subject", width: 25 },
    { header: "Order ID", key: "orderId", width: 15 },
    { header: "Message", key: "message", width: 40 },
    { header: "Status", key: "status", width: 12 },
    { header: "Created", key: "createdAt", width: 20 },
    { header: "Admin Notes", key: "adminNotes", width: 30 },
  ];

  const mapCtRow = (item) => ({
    name: item?.name || "-",
    email: item?.email || "-",
    phone: item?.contactNo || item?.phone || "-",
    subject: item?.subject || "-",
    orderId: item?.orderId || "-",
    message: item?.message || "-",
    status: item?.status || "-",
    createdAt: item?.createdAt ? moment(item.createdAt).format("DD/MM/YYYY HH:mm") : "-",
    adminNotes: (item?.adminNotes || []).map((n) => `[${n.adminName}] ${n.note}`).join(" | ") || "-",
  });

  const handleExportAll = async () => {
    setExporting(true);
    setShowExportProgress(true);
    setExportProgress(0);
    setExportStatus("fetching");
    try {
      const isPdp = activeTab === "pdp";
      const fetchFn = isPdp ? InquiryService.getInquiries : InquiryService.getContacts;
      const baseQ = isPdp ? { q: pdpSearch, status: pdpStatus, inquiryType: pdpInquiryType } : { q: ctSearch, status: ctStatus };
      setExportProgress(20);
      const res = await fetchFn({ ...baseQ, page: 1, limit: 50000, export: "true" });
      const items = res?.data?.results || [];
      if (!items.length) {
        message.showToast("No data to export", ToastTypes.WARNING);
        setShowExportProgress(false);
        return;
      }
      setExportProgress(30);
      setExportStatus("processing");
      const rows = items.map(isPdp ? mapPdpRow : mapCtRow);
      await buildExcelAndDownload(
        rows,
        isPdp ? pdpExcelColumns : ctExcelColumns,
        `${isPdp ? "inquiries" : "contacts"}_all_${Date.now()}`,
        ({ progress, status }) => { setExportProgress(progress); setExportStatus(status); }
      );
      message.showToast(`Exported ${items.length} records`, ToastTypes.SUCCESS);
    } catch (err) {
      message.showToast(err?.message || "Export failed", ToastTypes.ERROR);
      setExportStatus("error");
    } finally {
      setExporting(false);
    }
  };

  const handleExportDateWise = async () => {
    if (!exportDateRange.startDate || !exportDateRange.endDate) {
      message.showToast("Please select start and end date", ToastTypes.WARNING);
      return;
    }
    setExporting(true);
    setShowExportProgress(true);
    setExportProgress(0);
    setExportStatus("fetching");
    try {
      const isPdp = activeTab === "pdp";
      const fetchFn = isPdp ? InquiryService.getInquiries : InquiryService.getContacts;
      const baseQ = isPdp ? { q: pdpSearch, status: pdpStatus, inquiryType: pdpInquiryType } : { q: ctSearch, status: ctStatus };
      setExportProgress(20);
      const res = await fetchFn({ ...baseQ, startDate: exportDateRange.startDate, endDate: exportDateRange.endDate, page: 1, limit: 50000, export: "true" });
      const items = res?.data?.results || [];
      if (!items.length) {
        message.showToast("No data found for selected date range", ToastTypes.WARNING);
        setShowExportProgress(false);
        return;
      }
      setExportProgress(30);
      setExportStatus("processing");
      const rows = items.map(isPdp ? mapPdpRow : mapCtRow);
      const dateStr = `${exportDateRange.startDate}_to_${exportDateRange.endDate}`;
      await buildExcelAndDownload(
        rows,
        isPdp ? pdpExcelColumns : ctExcelColumns,
        `${isPdp ? "inquiries" : "contacts"}_${dateStr}_${Date.now()}`,
        ({ progress, status }) => { setExportProgress(progress); setExportStatus(status); }
      );
      message.showToast(`Exported ${items.length} records`, ToastTypes.SUCCESS);
    } catch (err) {
      message.showToast(err?.message || "Export failed", ToastTypes.ERROR);
      setExportStatus("error");
    } finally {
      setExporting(false);
    }
  };

  /* ═══════════════ PDP columns ═══════════════ */
  const pdpColumns = useMemo(() => [
    {
      key: "user",
      label: "User",
      width: 200,
      render: (_, row) => (
        <div className="user-cell">
          <p>{row?.user?.fullName || row?.user?.firstName || "N/A"}</p>
          <span>{row?.user?.email || "-"}</span>
        </div>
      ),
    },
    {
      key: "productImage",
      label: "Image",
      width: 60,
      render: (_, row) => (
        <div className="product-thumb">
          <img
            src={row?.productSnapshot?.imageUrl || row?.product?.imageUrl || NoImage}
            alt="product"
            referrerPolicy="no-referrer"
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = NoImage; }}
          />
        </div>
      ),
    },
    {
      key: "totalQuantity",
      label: "Total Qty",
      width: 100,
      render: (v) => <span className="qty-cell">{v ?? "-"}</span>,
    },
    {
      key: "productSku",
      label: "SKU",
      width: 160,
      render: (v, row) => (
        <span className="sku-cell sku-with-info">
          {v || "-"}
          {row?.inquiryType === "Error Query" && (
            <span
              className="sku-info-wrap"
              onMouseEnter={(e) => {
                const tip = e.currentTarget.querySelector(".sku-info-tooltip");
                if (!tip) return;
                const rect = e.currentTarget.getBoundingClientRect();
                tip.style.top = `${rect.top - 8}px`;
                tip.style.left = `${rect.left + rect.width / 2}px`;
                tip.style.transform = "translate(-50%, -100%)";
              }}
            >
              <FaInfoCircle className="sku-info-icon sku-info-error" />
              <span className="sku-info-tooltip">Dimensions not available in this product</span>
            </span>
          )}
        </span>
      ),
    },
    {
      key: "lastMessage",
      label: "Last Message",
      width: 260,
      render: (v) => (
        <span className="message-col-text">
          {v && v.length > 80 ? `${v.slice(0, 80)}...` : v || "-"}
        </span>
      ),
    },
    {
      key: "adminNotes",
      label: "Admin Note",
      width: 120,
      render: (v, row) => {
        const hasNotes = Array.isArray(v) && v.length > 0;
        return (
          <button
            type="button"
            className={`inq-note-btn ${hasNotes ? "has-notes" : ""}`}
            onClick={(e) => { e.stopPropagation(); openNotes(row, "inquiry"); }}
          >
            {hasNotes ? "View Note" : "Add Note"}
          </button>
        );
      },
    },
    {
      key: "status",
      label: "Status",
      width: 110,
      render: (v) => <span className={`status-badge ${statusBadgeClass(v)}`}>{v || "NA"}</span>,
    },
    {
      key: "createdAt",
      label: "Inquiry Date",
      width: 140,
      render: (v) => (
        <span className="date-col">{formatDate(v)}</span>
      ),
    },
    {
      key: "lastMessageAt",
      label: "Status Date",
      width: 140,
      render: (v, row) => (
        <span className="date-col">{formatDate(v || row?.updatedAt)}</span>
      ),
    },
    {
      key: "actions",
      label: "Action",
      width: 80,
      render: (_, row) => (
        <button
          type="button"
          className="link-btn"
          onClick={(e) => { e.stopPropagation(); navigate(`/admin/inquiries/${row._id || row.id}`); }}
        >
          View
        </button>
      ),
    },
  ], []);

  /* ═══════════════ Contact columns ═══════════════ */
  const ctColumns = useMemo(() => [
    {
      key: "name",
      label: "User",
      width: 200,
      render: (v, row) => (
        <div className="user-cell">
          <p>{v || "N/A"}</p>
          <span>{row?.email || "-"}</span>
        </div>
      ),
    },
    {
      key: "contactNo",
      label: "Phone",
      width: 130,
      render: (v, row) => v || row?.phone || "-",
    },
    {
      key: "subject",
      label: "Subject",
      width: 200,
      render: (v) => v || "-",
    },
    {
      key: "orderId",
      label: "Order ID",
      width: 130,
      render: (v) => v || "-",
    },
    {
      key: "status",
      label: "Status",
      width: 110,
      render: (v) => (
        <span className={`status-badge ${statusBadgeClass((v || "open").toLowerCase())}`}>
          {v || "open"}
        </span>
      ),
    },
    {
      key: "message",
      label: "Message",
      width: 260,
      render: (v) => (
        <span className="message-col-text" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {v || "-"}
        </span>
      ),
    },
    {
      key: "adminNotes",
      label: "Admin Note",
      width: 120,
      render: (v, row) => {
        const hasNotes = Array.isArray(v) && v.length > 0;
        return (
          <button
            type="button"
            className={`inq-note-btn ${hasNotes ? "has-notes" : ""}`}
            onClick={(e) => { e.stopPropagation(); openNotes(row, "contact"); }}
          >
            {hasNotes ? "View Note" : "Add Note"}
          </button>
        );
      },
    },
    {
      key: "createdAt",
      label: "Created",
      width: 140,
      render: (v) => <span className="date-col">{v ? formatDate(v) : "-"}</span>,
    },
    {
      key: "actions",
      label: "Action",
      width: 80,
      render: (_, row) => (
        <button
          type="button"
          className="link-btn"
          onClick={(e) => { e.stopPropagation(); navigate(`/admin/contacts/${row._id}`); }}
        >
          View
        </button>
      ),
    },
  ], []);

  /* ═══════════════ Toolbar builders ═══════════════ */
  const isPdp = activeTab === "pdp";
  const search = isPdp ? pdpSearch : ctSearch;
  const setSearch = isPdp ? setPdpSearch : setCtSearch;
  const statusVal = isPdp ? pdpStatus : ctStatus;
  const setStatusVal = isPdp ? setPdpStatus : setCtStatus;
  const doFetch = isPdp ? fetchPdp : fetchCt;
  const setPageFn = isPdp ? setPdpPage : setCtPage;
  const curLimit = isPdp ? pdpLimit : ctLimit;
  const setLimitFn = isPdp ? setPdpLimit : setCtLimit;
  const totalItems = isPdp ? pdpTotal : ctTotal;

  const toolbarLeft = (
    <div className="um-toolbar-left">
      <div className="um-search-box">
        <FaSearch className="um-search-icon" />
        <input
          type="text"
          className="um-search-input"
          placeholder={isPdp ? "Search SKU, message, user email..." : "Search name, email, subject, order ID..."}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyUp={(e) => { if (e.key === "Enter") { setPageFn(1); doFetch(); } }}
        />
      </div>
      <select className="um-filter-select" value={statusVal} onChange={(e) => { setStatusVal(e.target.value); setPageFn(1); }}>
        <option value="">All Status</option>
        <option value="open">Open</option>
        <option value="responded">Responded</option>
        <option value="closed">Closed</option>
      </select>
      {isPdp && (
        <select className="um-filter-select" value={pdpInquiryType} onChange={(e) => { setPdpInquiryType(e.target.value); setPdpPage(1); }}>
          <option value="">All Types</option>
          <option value="error_query">Price Error Inquiry</option>
          <option value="general_query">General Inquiry</option>
        </select>
      )}
      <button className="um-search-btn" onClick={() => { setPageFn(1); doFetch(); }}>
        <FaSearch /> Search
      </button>
      {/* <div className="um-total-users" style={{ display: "flex", alignItems: "center", padding: "0 12px", fontSize: "13px", fontWeight: "600", color: "#24292f", whiteSpace: "nowrap" }}>
        Total: <span style={{ color: "#0969da", marginLeft: 4 }}>{totalItems}</span>
      </div> */}
    </div>
  );

  const toolbarRight = (
    <div className="um-toolbar-right">
      <button className="um-refresh-btn" onClick={() => doFetch()} title="Refresh">
        <FaSync className={(isPdp ? pdpLoading : ctLoading) ? "um-spin" : ""} />
      </button>
      <div className="um-rows-picker">
        <span>Rows:</span>
        <select value={curLimit} onChange={(e) => { setLimitFn(Number(e.target.value)); setPageFn(1); }}>
          {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
    </div>
  );

  const customExportOptions = useMemo(() => [
    {
      type: "date-range",
      dateRangeComponent: (
        <div style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}>
          <label style={{ display: "block", marginBottom: 6, fontSize: 12, fontWeight: 600, color: "#24292f" }}>Date Range Export</label>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input type="date" value={exportDateRange.startDate} onChange={(e) => setExportDateRange((p) => ({ ...p, startDate: e.target.value }))} style={{ flex: 1, padding: 6, fontSize: 12, border: "1px solid #d0d7de", borderRadius: 4 }} />
            <input type="date" value={exportDateRange.endDate} onChange={(e) => setExportDateRange((p) => ({ ...p, endDate: e.target.value }))} style={{ flex: 1, padding: 6, fontSize: 12, border: "1px solid #d0d7de", borderRadius: 4 }} />
          </div>
          <button onClick={handleExportDateWise} disabled={exporting || !exportDateRange.startDate || !exportDateRange.endDate} style={{ width: "100%", padding: "6px 8px", backgroundColor: "#4CAF50", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, height: 32 }}>
            <FaCalendarAlt /> Export Date Wise
          </button>
        </div>
      ),
    },
    { type: "divider" },
    {
      type: "custom",
      component: (
        <div style={{ padding: 8 }}>
          <button onClick={handleExportAll} disabled={exporting} style={{ width: "100%", padding: "6px 8px", backgroundColor: "#2196F3", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, height: 32 }}>
            <FaFileExport /> Export All
          </button>
        </div>
      ),
    },
  ], [exportDateRange, exporting, activeTab]);

  /* ═══════════════ Pagination ═══════════════ */
  const curPage = isPdp ? pdpPage : ctPage;
  const totalPagesVal = isPdp ? pdpPages : ctPages;
  const startItem = (curPage - 1) * curLimit + 1;
  const endItem = Math.min(curPage * curLimit, totalItems);

  /* ═══════════════ Render ═══════════════ */
  return (
    <div className="admin-inquiries-page">
      {/* Page Header + Tabs */}
      <div className="inq-page-header">
        <div className="inq-page-title-section">
          <h1 className="inq-page-title">Inquiries</h1>
          <p className="inq-page-subtitle">Manage product inquiries and contact requests</p>
        </div>
        <div className="inq-tabs-bar">
          <button type="button" className={`inq-tab ${activeTab === "pdp" ? "inq-tab-active" : ""}`} onClick={() => navigate("/admin/inquiries")}>
            <FaBoxOpen className="inq-tab-icon" />
            <span>PDP Inquiry</span>
            {pdpTotal > 0 && <span className="inq-tab-count">{pdpTotal}</span>}
          </button>
          <button type="button" className={`inq-tab ${activeTab === "contact" ? "inq-tab-active" : ""}`} onClick={() => navigate("/admin/contacts")}>
            <FaEnvelope className="inq-tab-icon" />
            <span>Contact Us</span>
            {ctTotal > 0 && <span className="inq-tab-count">{ctTotal}</span>}
          </button>
        </div>
      </div>

      {/* ExcelTable */}
      <div className="um-table-section">
        <ExcelTable
          key={activeTab}
          isLoading={isPdp ? pdpLoading : ctLoading}
          loadingText={isPdp ? "Loading inquiries..." : "Loading contacts..."}
          data={isPdp ? pdpData : ctData}
          columns={isPdp ? pdpColumns : ctColumns}
          showToolbarExport={true}
          showToolbarFilter={false}
          showToolbarColumns={true}
          showToolbarFullscreen={true}
          toolbarLeftActions={toolbarLeft}
          toolbarRightActions={toolbarRight}
          selectable={false}
          sortable={true}
          resizable={true}
          showToolbar={true}
          title={isPdp ? "PDP Inquiries" : "Contact Inquiries"}
          onRowClick={(row) => navigate(isPdp ? `/admin/inquiries/${row._id || row.id}` : `/admin/contacts/${row._id}`)}
          userId={currentUserId}
          customExportOptions={customExportOptions}
        />

        {totalItems > 0 && (
          <div className="um-pagination">
            <div className="um-pagination-info">
              Showing {startItem} to {endItem} of {totalItems}
            </div>
            <div className="um-pagination-controls">
              <button className="um-page-btn" disabled={curPage <= 1} onClick={() => setPageFn(curPage - 1)}>‹</button>
              {Array.from({ length: Math.min(5, totalPagesVal) }, (_, i) => {
                let pn;
                if (totalPagesVal <= 5) pn = i + 1;
                else if (curPage <= 3) pn = i + 1;
                else if (curPage >= totalPagesVal - 2) pn = totalPagesVal - 4 + i;
                else pn = curPage - 2 + i;
                return (
                  <button key={pn} className={`um-page-btn ${curPage === pn ? "um-page-btn-active" : ""}`} onClick={() => setPageFn(pn)}>
                    {pn}
                  </button>
                );
              })}
              {totalPagesVal > 5 && curPage < totalPagesVal - 2 && (
                <>
                  <span className="um-page-dots">...</span>
                  <button className="um-page-btn" onClick={() => setPageFn(totalPagesVal)}>{totalPagesVal}</button>
                </>
              )}
              <button className="um-page-btn" disabled={curPage >= totalPagesVal} onClick={() => setPageFn(curPage + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {/* Notes modal */}
      <InquiryNotesModal
        open={notesModal.open}
        onClose={() => setNotesModal((p) => ({ ...p, open: false }))}
        itemId={notesModal.itemId}
        adminNotes={notesModal.notes}
        type={notesModal.type}
        onNotesUpdated={handleNotesUpdated}
      />

      {/* Export progress */}
      <ExportProgress
        progress={exportProgress}
        isExporting={showExportProgress}
        status={exportStatus}
        onClose={() => { setShowExportProgress(false); setExportProgress(0); setExportStatus("fetching"); }}
      />
    </div>
  );
};

export default AdminInquiryList;

